import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Spin, Input, Popconfirm, Tooltip } from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  FolderAddOutlined,
  PlusOutlined,
  DeleteOutlined,
  RightOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';

export type FolderTreeNode = {
  id: number;
  parentId: number | null;
  name: string;
};

export type FolderTreeProps = {
  folders: FolderTreeNode[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  onCreate: (parentId: number | null, name: string) => Promise<void> | void;
  onDelete?: (id: number) => Promise<void> | void;
  loading?: boolean;
  title?: React.ReactNode;
  emptyText?: string;
  showDelete?: boolean;
  bodyStyle?: React.CSSProperties;
  // When false, hides all folder-mutation controls (header "+ New folder",
  // per-row "+ sub-folder", per-row delete) regardless of showDelete.
  canMutate?: boolean;
};

const COLORS = {
  border: '#E2E8F1',
  folder: '#E8A932',
  folderActive: '#1677FF',
  chevron: '#94A3B8',
  labelDefault: '#64748B',
  labelHover: '#0F172A',
  labelActive: '#1677FF',
  bgHover: '#F1F5F9',
  bgActive: '#E6F4FF',
  actionDefault: '#94A3B8',
  actionAdd: '#16A34A',
  actionAddBg: '#F0FDF4',
  actionDelete: '#DC2626',
  actionDeleteBg: '#FEF2F2',
};

type InternalNode = FolderTreeNode & { children: InternalNode[] };

const buildTree = (folders: FolderTreeNode[]): InternalNode[] => {
  const byId = new Map<number, InternalNode>();
  folders.forEach(f => byId.set(f.id, { ...f, children: [] }));
  const roots: InternalNode[] = [];
  folders.forEach(f => {
    const node = byId.get(f.id)!;
    if (f.parentId != null && byId.has(f.parentId)) {
      byId.get(f.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
};

type ActionButtonProps = {
  icon: React.ReactNode;
  tooltip: string;
  onClick?: (e: React.MouseEvent) => void;
  hoverColor: string;
  hoverBg?: string;
  defaultColor?: string;
};

const ActionButton: React.FC<ActionButtonProps> = ({
  icon,
  tooltip,
  onClick,
  hoverColor,
  hoverBg,
  defaultColor = COLORS.actionDefault,
}) => {
  const [hover, setHover] = useState(false);
  return (
    <Tooltip title={tooltip}>
      <span
        role="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: 26,
          height: 26,
          borderRadius: 4,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: 13,
          color: hover ? hoverColor : defaultColor,
          background: hover ? hoverBg || 'transparent' : 'transparent',
          border: 'none',
          transition: 'background 0.15s ease, color 0.15s ease',
        }}
      >
        {icon}
      </span>
    </Tooltip>
  );
};

const HeaderNewFolderButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: hover ? COLORS.bgHover : 'transparent',
        color: hover ? COLORS.labelHover : COLORS.labelDefault,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 4,
        padding: '3px 10px',
        fontSize: 12,
        lineHeight: 1.4,
        cursor: 'pointer',
        transition: 'background 0.15s ease, color 0.15s ease',
      }}
    >
      <FolderAddOutlined />
      <span>New folder</span>
    </button>
  );
};

type InlineCreateRowProps = {
  value: string;
  onChange: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

const InlineCreateRow: React.FC<InlineCreateRowProps> = ({ value, onChange, onConfirm, onCancel }) => (
  <div
    onClick={e => e.stopPropagation()}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 6px',
    }}
  >
    <span style={{ width: 14, height: 14, display: 'inline-block' }} />
    <span style={{ color: COLORS.folder, display: 'inline-flex', alignItems: 'center' }}>
      <FolderOutlined />
    </span>
    <Input
      size="small"
      autoFocus
      value={value}
      onChange={e => onChange(e.target.value)}
      onPressEnter={onConfirm}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
      placeholder="Folder name"
      style={{ flex: 1, fontSize: 12 }}
    />
    <ActionButton
      icon={<CheckOutlined />}
      tooltip="Create"
      onClick={e => {
        e.stopPropagation();
        onConfirm();
      }}
      hoverColor={COLORS.actionAdd}
      hoverBg={COLORS.actionAddBg}
    />
    <ActionButton
      icon={<CloseOutlined />}
      tooltip="Cancel"
      onClick={e => {
        e.stopPropagation();
        onCancel();
      }}
      hoverColor={COLORS.actionDelete}
      hoverBg={COLORS.actionDeleteBg}
    />
  </div>
);

type FolderRowProps = {
  node: InternalNode;
  selectedId: number | null;
  expandedIds: Set<number>;
  creatingUnder: number | 'root' | null;
  newName: string;
  showDelete: boolean;
  canMutate: boolean;
  onSelect: (id: number) => void;
  onToggleExpand: (id: number) => void;
  onStartCreate: (parentId: number) => void;
  onCancelCreate: () => void;
  onConfirmCreate: () => void;
  onNewNameChange: (name: string) => void;
  onDelete?: (id: number) => void;
};

const FolderRow: React.FC<FolderRowProps> = ({
  node,
  selectedId,
  expandedIds,
  creatingUnder,
  newName,
  showDelete,
  canMutate,
  onSelect,
  onToggleExpand,
  onStartCreate,
  onCancelCreate,
  onConfirmCreate,
  onNewNameChange,
  onDelete,
}) => {
  const [hover, setHover] = useState(false);
  const isSelected = selectedId === node.id;
  const expanded = expandedIds.has(node.id);
  const isCreatingHere = creatingUnder === node.id;
  const hasChildren = node.children.length > 0;
  const showChildrenContainer = hasChildren || isCreatingHere;
  const canToggle = hasChildren || isCreatingHere;

  const labelColor = isSelected ? COLORS.labelActive : '#000000';
  const rowBg = isSelected ? COLORS.bgActive : hover ? COLORS.bgHover : 'transparent';
  const folderColor = isSelected ? COLORS.folderActive : COLORS.folder;
  const actionsVisible = hover || isSelected;

  return (
    <div>
      <div
        onClick={() => onSelect(node.id)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 6px',
          borderRadius: 4,
          cursor: 'pointer',
          background: rowBg,
          transition: 'background 0.15s ease',
          userSelect: 'none',
        }}
      >
        <span
          onClick={e => {
            e.stopPropagation();
            if (canToggle) onToggleExpand(node.id);
          }}
          style={{
            width: 14,
            height: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.chevron,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            visibility: canToggle ? 'visible' : 'hidden',
            cursor: canToggle ? 'pointer' : 'default',
          }}
        >
          <RightOutlined style={{ fontSize: 10 }} />
        </span>
        <span
          style={{
            color: folderColor,
            display: 'inline-flex',
            alignItems: 'center',
            transition: 'color 0.15s ease',
          }}
        >
          {expanded && showChildrenContainer ? <FolderOpenOutlined /> : <FolderOutlined />}
        </span>
        <span
          title={node.name}
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 500,
            color: labelColor,
            transition: 'color 0.15s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {node.name}
        </span>
        <span
          style={{
            display: canMutate ? 'inline-flex' : 'none',
            gap: 2,
            opacity: actionsVisible ? 1 : 0,
            transition: 'opacity 0.15s ease',
            pointerEvents: actionsVisible ? 'auto' : 'none',
          }}
        >
          <ActionButton
            icon={<PlusOutlined />}
            tooltip="Add sub-folder"
            onClick={e => {
              e.stopPropagation();
              onStartCreate(node.id);
            }}
            hoverColor={COLORS.actionAdd}
            hoverBg={COLORS.actionAddBg}
          />
          {showDelete && onDelete && (
            <Popconfirm
              title="Delete this folder?"
              description="Folder must be empty to delete."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={e => {
                e?.stopPropagation?.();
                onDelete(node.id);
              }}
              onCancel={e => e?.stopPropagation?.()}
            >
              <span
                onClick={e => e.stopPropagation()}
                style={{ display: 'inline-flex' }}
              >
                <ActionButton
                  icon={<DeleteOutlined />}
                  tooltip="Delete folder"
                  hoverColor={COLORS.actionDelete}
                  hoverBg={COLORS.actionDeleteBg}
                />
              </span>
            </Popconfirm>
          )}
        </span>
      </div>
      {showChildrenContainer && (
        <div
          style={{
            marginLeft: 22,
            paddingLeft: 12,
            borderLeft: `1.5px solid ${COLORS.border}`,
            overflow: 'hidden',
            maxHeight: expanded ? 2000 : 0,
            transition: 'max-height 0.25s ease',
          }}
        >
          {node.children.map(child => (
            <FolderRow
              key={child.id}
              node={child}
              selectedId={selectedId}
              expandedIds={expandedIds}
              creatingUnder={creatingUnder}
              newName={newName}
              showDelete={showDelete}
              canMutate={canMutate}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onStartCreate={onStartCreate}
              onCancelCreate={onCancelCreate}
              onConfirmCreate={onConfirmCreate}
              onNewNameChange={onNewNameChange}
              onDelete={onDelete}
            />
          ))}
          {isCreatingHere && (
            <InlineCreateRow
              value={newName}
              onChange={onNewNameChange}
              onConfirm={onConfirmCreate}
              onCancel={onCancelCreate}
            />
          )}
        </div>
      )}
    </div>
  );
};

const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  selectedId,
  onSelect,
  onCreate,
  onDelete,
  loading,
  title = 'My Library',
  emptyText = 'No folders yet',
  showDelete = false,
  bodyStyle,
  canMutate = true,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [creatingUnder, setCreatingUnder] = useState<number | 'root' | null>(null);
  const [newName, setNewName] = useState('');
  const seenIdsRef = useRef<Set<number>>(new Set());

  const treeData = useMemo(() => buildTree(folders), [folders]);

  useEffect(() => {
    const newlySeen: number[] = [];
    folders.forEach(f => {
      if (!seenIdsRef.current.has(f.id)) {
        seenIdsRef.current.add(f.id);
        newlySeen.push(f.id);
      }
    });
    if (newlySeen.length > 0) {
      setExpandedIds(prev => {
        const next = new Set(prev);
        newlySeen.forEach(id => next.add(id));
        return next;
      });
    }
  }, [folders]);

  const toggleExpand = (id: number) =>
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const startCreate = (parentId: number | 'root') => {
    setCreatingUnder(parentId);
    setNewName('');
    if (typeof parentId === 'number') {
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.add(parentId);
        return next;
      });
    }
  };

  const cancelCreate = () => {
    setCreatingUnder(null);
    setNewName('');
  };

  const confirmCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    const parentId = creatingUnder === 'root' ? null : (creatingUnder as number);
    try {
      await onCreate(parentId, name);
      setCreatingUnder(null);
      setNewName('');
    } catch {
      /* parent surfaces error; leave input open */
    }
  };

  const isEmpty = folders.length === 0 && creatingUnder !== 'root';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <strong style={{ color: COLORS.labelHover, fontSize: 14 }}>{title}</strong>
        {canMutate && <HeaderNewFolderButton onClick={() => startCreate('root')} />}
      </div>

      <div style={bodyStyle}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : isEmpty ? (
          <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div>
            {treeData.map(node => (
              <FolderRow
                key={node.id}
                node={node}
                selectedId={selectedId}
                expandedIds={expandedIds}
                creatingUnder={creatingUnder}
                newName={newName}
                showDelete={showDelete && canMutate}
                canMutate={canMutate}
                onSelect={onSelect}
                onToggleExpand={toggleExpand}
                onStartCreate={startCreate}
                onCancelCreate={cancelCreate}
                onConfirmCreate={confirmCreate}
                onNewNameChange={setNewName}
                onDelete={onDelete}
              />
            ))}
            {creatingUnder === 'root' && (
              <InlineCreateRow
                value={newName}
                onChange={setNewName}
                onConfirm={confirmCreate}
                onCancel={cancelCreate}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderTree;
